import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createProjectDocumentWorkflow,
  getProjectDocumentWorkflow,
  getProjectDocumentWorkflowActionItems,
  getProjectDocumentWorkflowMyDocuments,
  stopProjectDocumentWorkflow,
  updateProjectDocumentWorkflow
} from '../../api/project-document-workflows';
import { getProjectDocumentRevisionApprovalAsync } from '../ProjectDocumentRevision/projectDocumentRevisionSlice';
import { WorkflowStep } from '../MasterData/WorkflowStep/workflowStepSlice';
import { User } from '../UserProfile/userProfileSlice';

export const getProjectDocumentWorkflowAsync = createAsyncThunk(
  'projectDocumentWorkflows/getProjectDocumentWorkflow',
  async (projectDocumentWorkflowId: number, thunkAPI) => {
    const projectDocumentWorkflow = await getProjectDocumentWorkflow(
      projectDocumentWorkflowId
    );
    thunkAPI.dispatch(
      getProjectDocumentRevisionApprovalAsync(
        projectDocumentWorkflow.projectDocumentRevisionId
      )
    );
    return projectDocumentWorkflow;
  }
);

export const createProjectDocumentWorkflowAsync = createAsyncThunk(
  'projectDocumentWorkflows/createProjectDocumentWorkflow',
  createProjectDocumentWorkflow
);

export const updateProjectDocumentWorkflowAsync = createAsyncThunk(
  'projectDocumentWorkflows/updateProjectDocumentWorkflow',
  updateProjectDocumentWorkflow
);

export const stopProjectDocumentWorkflowAsync = createAsyncThunk(
  'projectDocumentWorkflows/stopProjectDocumentWorkflow',
  stopProjectDocumentWorkflow
);

export const getProjectDocumentWorkflowActionItemsAsync = createAsyncThunk(
  'projectDocumentWorkflows/getProjectDocumentWorkflowActionItems',
  getProjectDocumentWorkflowActionItems
);

export const getProjectDocumentWorkflowMyDocumentsAsync = createAsyncThunk(
  'projectDocumentWorkflows/getProjectDocumentWorkflowMyDocuments',
  getProjectDocumentWorkflowMyDocuments
);

export interface ProjectDocumentWorkflow {
  id: number;
  name: string;
  linkName: string | null;
  linkUrl: string | null;
  isAutoApproval: boolean | null;
  isCreatorViewOnlyExternal: boolean | null;
  isCreatorViewOnlyFinal: boolean | null;
  projectDocumentRevisionId: number;
  createdAt: Date;
  finishedDate: Date;
  workflowStatusId: number;
  workflowStatus: {
    id: number;
    name: string;
  };
  creatorId: number | null;
}

export interface DocumentWorkflowWithStepAndApproval
  extends ProjectDocumentWorkflow {
  currentWorkflowStepId: number;
  currentWorkflowStep: {
    id: number;
    dueDate: Date;
    workflowStepId: number;
    workflowStep: WorkflowStep;
    userApprovals: {
      id: number;
      userId: number;
      user: User;
      approvalTypeId: number | null;
      approvalCodeId: number | null;
      comment: string | null;
      commentFiles: {
        id: number;
        name: string;
        s3ContentType: string;
      }[];
      isAutoApproval: boolean | null;
      autoApprovalNote: string | null;
      submittedDate: Date;
    }[];
  };
  projectDocumentWorkflowSteps: {
    id: number;
    dueDate: Date;
    workflowStepId: number;
    workflowStep: WorkflowStep;
    userApprovals: {
      id: number;
      userId: number;
      approvalTypeId: number | null;
      approvalCodeId: number | null;
      comment: string | null;
      commentFiles: {
        id: number;
        name: string;
        s3ContentType: string;
      }[];
      isAutoApproval: boolean | null;
      autoApprovalNote: string | null;
      checklistUserApproval: {
        id: number;
        isChecked: boolean;
        linkUrl: string | null;
        checklistUserApprovalFiles: {
          id: number;
          name: string;
          s3ContentType: string;
        }[];
      } | null;
      submittedDate: Date;
      user: User;
    }[];
  }[];
  checklistCreator: {
    id: number;
    isChecked: boolean;
    linkUrl: string | null;
    checklistCreatorFiles: {
      id: number;
      name: string;
      s3ContentType: string;
    }[];
  } | null;
}

export interface ProjectDocumentWorkflowMyTask {
  id: number;
  name: string;
  workflowStatusId: number;
  projectDocumentRevisionId: number;
  projectDocumentRevision: {
    id: number;
    planIssueDate: Date; //TODO Revised Type
    projectDocumentId: number;
    projectDocument: {
      id: number;
      no: string;
      name: string;
      disciplineId: number;
      projectId: number;
      project: {
        id: number;
        projectNo: string;
        name: string;
        projectRegisterId: number;
        projectRegister: {
          id: number;
          projectNo: string;
          name: string;
          plantName: string;
        };
      };
    };
    purpose: {
      id: number;
      abbreviation: string;
      phase: string;
    };
    revision: {
      id: number;
      name: string;
    };
  };
  currentWorkflowStepId: number;
  currentWorkflowStep: {
    id: number;
    dueDate: string;
    projectDocumentWorkflowId: number;
    workflowStepId: number;
    workflowStep: {
      id: number;
      name: string;
    };
    userApprovals: [
      {
        id: number;
        userId: number;
      }
    ];
  };
}

interface ProjectDocumentWorkflowState {
  selectedProjectDocumentWorkflow: DocumentWorkflowWithStepAndApproval | null;
  actionItems: {
    data: ProjectDocumentWorkflowMyTask[];
    count: number;
    countOnDue: number;
    countDelay: number;
  };
  myDocuments: {
    data: ProjectDocumentWorkflowMyTask[];
    count: number;
    countOnDue: number;
    countDelay: number;
  };
}

const initialState: ProjectDocumentWorkflowState = {
  selectedProjectDocumentWorkflow: null,
  actionItems: {
    data: [],
    count: 0,
    countOnDue: 0,
    countDelay: 0
  },
  myDocuments: {
    data: [],
    count: 0,
    countOnDue: 0,
    countDelay: 0
  }
};

export default createSlice({
  name: 'projectDocumentWorkflows',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getProjectDocumentWorkflowAsync.fulfilled,
      (state, action) => {
        state.selectedProjectDocumentWorkflow = action.payload;
      }
    );
    builder.addCase(
      getProjectDocumentWorkflowActionItemsAsync.fulfilled,
      (state, action) => {
        state.actionItems = action.payload;
      }
    );
    builder.addCase(
      getProjectDocumentWorkflowMyDocumentsAsync.fulfilled,
      (state, action) => {
        state.myDocuments = action.payload;
      }
    );
  }
});
